import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Loader2, Wifi, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationContext';
import { useTask } from '../../contexts/TaskContext';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const NotificationMenu = () => {
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    ensurePushSubscription,
    pushSupport
  } = useNotifications();
  const { openTaskDetail } = useTask();

  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const swipeRefs = useRef({});
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());
  
  const [notificationPermission, setNotificationPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'default'
  );
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleOpenPanel = () => {
      setOpen(true);
    };
    window.addEventListener('openNotificationPanel', handleOpenPanel);
    return () => window.removeEventListener('openNotificationPanel', handleOpenPanel);
  }, []);

  const handleTouchStart = useCallback((notificationId, e) => {
    if (!e.touches || !e.touches[0]) return;
    const touch = e.touches[0];
    swipeRefs.current[notificationId] = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now()
    };
  }, []);

  const handleTouchMove = useCallback((notificationId, e) => {
    if (!swipeRefs.current[notificationId] || !e.touches || !e.touches[0]) return;
    e.preventDefault();
    const touch = e.touches[0];
    const deltaX = touch.clientX - swipeRefs.current[notificationId].startX;
    const deltaY = touch.clientY - swipeRefs.current[notificationId].startY;
    
    if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX < 0) {
      const element = e.currentTarget;
      if (element) {
        const translateX = Math.max(deltaX, -120);
        element.style.transform = `translateX(${translateX}px)`;
        element.style.transition = 'none';
      }
    }
  }, []);

  const handleTouchEnd = useCallback((notificationId, e) => {
    if (!swipeRefs.current[notificationId] || !e.changedTouches || !e.changedTouches[0]) return;
    const touch = swipeRefs.current[notificationId];
    const deltaX = e.changedTouches[0].clientX - touch.startX;
    const deltaY = e.changedTouches[0].clientY - touch.startY;
    const deltaTime = Date.now() - touch.startTime;
    
    const element = e.currentTarget;
    if (!element) {
      delete swipeRefs.current[notificationId];
      return;
    }
    
    element.style.transition = 'transform 0.3s ease-out';
    
    if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX < -100 && deltaTime < 300) {
      element.style.transform = 'translateX(-100%)';
      element.style.opacity = '0';
      setTimeout(() => {
        deleteNotification(notificationId);
      }, 300);
    } else {
      element.style.transform = 'translateX(0)';
    }
    
    delete swipeRefs.current[notificationId];
  }, [deleteNotification]);
  
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    setNotificationPermission(Notification.permission);
    const interval = setInterval(() => {
      setNotificationPermission(Notification.permission);
    }, 2000);
    return () => clearInterval(interval);
  }, []);
  
  const isPushActive = pushSupport?.supported && notificationPermission === 'granted';
  const hasBasicPushSupport = typeof window !== 'undefined' && 
    'serviceWorker' in navigator && 
    'Notification' in window;
  const shouldShowPushButton = !isPushActive && (pushSupport?.supported || hasBasicPushSupport);

  const handleItemClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    
    // Görev bildirimlerini kontrol et
    if (notification.metadata?.type === 'task' && notification.metadata?.taskId) {
      openTaskDetail(notification.metadata.taskId);
      setOpen(false);
      return;
    }
    
    // Müşteri siparişi bildirimleri için yönlendirme
    if (notification.type === 'customer_order' && notification.metadata?.redirectUrl) {
      navigate(notification.metadata.redirectUrl);
      setOpen(false);
      return;
    }
    
    // Mesajı göster/gizle
    setExpandedNotifications((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(notification.id)) {
        newSet.delete(notification.id);
      } else {
        newSet.add(notification.id);
      }
      return newSet;
    });
    
    if (notification.metadata?.url || notification.metadata?.redirectUrl) {
      navigate(notification.metadata?.url || notification.metadata?.redirectUrl);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        className={`relative p-2 rounded-lg transition-all duration-200 ${
          unreadCount > 0
            ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 ring-2 ring-red-500/50 hover:bg-red-100 dark:hover:bg-red-900/30'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Bildirimler"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <>
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-pulse z-10">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full animate-ping opacity-75" />
          </>
        )}
      </button>

      {open && (
        <div className="fixed sm:absolute right-2 sm:right-0 top-16 sm:top-auto sm:mt-2 w-[calc(100vw-1rem)] sm:w-80 md:w-96 max-w-[calc(100vw-1rem)] sm:max-w-none bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-[80vh] flex flex-col">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 rounded-t-2xl flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Bildirimler</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {unreadCount > 0 ? `${unreadCount} okunmamış bildirim` : 'Tüm bildirimler okundu'}
              </p>
            </div>
            <button
              onClick={() => unreadCount > 0 && markAllAsRead()}
              disabled={!unreadCount}
              className={`text-xs px-2 py-1 rounded-md border ${
                unreadCount
                  ? 'text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                  : 'text-gray-400 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed'
              }`}
            >
              Tümünü okundu yap
            </button>
          </div>

          {shouldShowPushButton && (
            <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 space-y-2">
              <div className="flex items-start gap-2">
                <Wifi className="w-4 h-4 text-blue-600 dark:text-blue-300 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-blue-800 dark:text-blue-200">Push bildirimlerini etkinleştir</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                    {pushSupport?.reason === 'iosStandalone'
                      ? 'iOS cihazlarda bildirim için Safari\'den "Paylaş > Ana Ekrana Ekle" ile uygulamayı yükleyip tekrar deneyin.'
                      : pushSupport?.reason === 'missingKey'
                      ? 'Push anahtarları tanımlı olmadığı için sunucu push gönderemiyor. Yönetici tarafından tamamlanmalı.'
                      : 'Uygun bir tarayıcıda (Chrome, Edge, Safari) izin vererek anlık bildirim alın.'}
                  </p>
                </div>
              </div>
              {(pushSupport?.supported || hasBasicPushSupport) ? (
                <button
                  className="mt-1 text-xs font-semibold text-blue-600 dark:text-blue-300 hover:underline"
                  onClick={async () => {
                    try {
                      await ensurePushSubscription();
                    } catch (error) {
                      toast.error(error.message || 'Bir hata oluştu');
                    }
                  }}
                >
                  Aktif et
                </button>
              ) : (
                <p className="text-[11px] text-blue-600 dark:text-blue-300">
                  {pushSupport?.reason === 'iosStandalone'
                    ? 'Ana ekrana ekledikten sonra uygulamayı açıp bildirim izni isteğini kabul etmelisiniz.'
                    : pushSupport?.reason === 'missingKey'
                    ? 'Sunucu tarafında VAPID anahtarları tanımlandığında otomatik olarak aktif hale gelir.'
                    : 'Push desteği olmayan tarayıcılarda yalnızca uygulama içi bildirimler gösterilir.'}
                </p>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500">
                Gösterilecek bildirim yok
              </div>
            ) : (
              notifications.map((notification) => {
                const isExpanded = expandedNotifications.has(notification.id);
                return (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 border-b border-gray-100 dark:border-gray-700 cursor-pointer transition ${
                      notification.isRead ? 'bg-white dark:bg-gray-800' : 'bg-blue-50/60 dark:bg-gray-900/40'
                    } hover:bg-gray-50 dark:hover:bg-gray-700 relative`}
                    onClick={(e) => {
                      if (!swipeRefs.current[notification.id]) {
                        handleItemClick(notification);
                      }
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      handleTouchStart(notification.id, e);
                    }}
                    onTouchMove={(e) => {
                      e.preventDefault();
                      handleTouchMove(notification.id, e);
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      handleTouchEnd(notification.id, e);
                    }}
                    style={{ touchAction: 'pan-x pan-y' }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {notification.title}
                        </p>
                        {isExpanded && (
                          <>
                            <p className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-line mt-2">
                              {notification.body}
                            </p>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
                              {formatDate(notification.createdAt)}
                            </p>
                          </>
                        )}
                        {!isExpanded && (
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                            {formatDate(notification.createdAt)}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!notification.isRead && (
                          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px]">
                            <Check className="w-3 h-3" />
                          </span>
                        )}
                        {notification.isRead && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            className="p-1.5 rounded-md hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition"
                            aria-label="Bildirimi sil"
                            title="Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default NotificationMenu;


